/// <reference types="bootstrap" />
/// <reference types="loading-visualization" />

import * as DOMPurify from "dompurify";
import {NotificationType} from "./Enums/NotificationType";
import type INotificationConfiguration from "./Interfaces/INotificationConfiguration";
import type ISpinnerConfiguration from "./Interfaces/ISpinnerConfiguration";
import NotificationConfiguration from "./NotificationConfiguration";

declare const jq: JQueryStatic;

export default class GuiCommonShared {
    private alertModalEl;
    private alertModalHeaderEl;
    private alertModalBodyEl;

    protected confirmationModalEl;
    protected confirmationModalHeaderEl;
    protected confirmationModalBodyEl;
    protected confirmationModalCancelButtonEl;
    protected confirmationModalConfirmButtonEl;

    private readonly mLocalization: Localization;
    private readonly jQueryInstance: JQueryStatic;

    public constructor(
        localization: Localization,
        jQuery: JQueryStatic | null = null,
    ) {
        this.mLocalization = localization;
        if (jQuery === null) {
            this.jQueryInstance = typeof jq === "undefined" ? $ : jq;
        } else {
            this.jQueryInstance = jQuery;
        }

        this.alertModalEl = this.jQueryInstance("#alertModal");
        this.alertModalHeaderEl = this.alertModalEl.find(".alert-modal-label");
        this.alertModalBodyEl = this.alertModalEl.find(".modal-body");

        this.confirmationModalEl = this.jQueryInstance("#page-confirm-modal");
        this.confirmationModalHeaderEl = this.confirmationModalEl.find(".modal-title");
        this.confirmationModalBodyEl = this.confirmationModalEl.find(".modal-body");
        this.confirmationModalCancelButtonEl = this.confirmationModalEl.find(".btn-cancel");
        this.confirmationModalConfirmButtonEl = this.confirmationModalEl.find(".btn-ok");
    }

    public showNotification(notificationConfiguration: INotificationConfiguration): HTMLElement {
        if (notificationConfiguration.body === undefined) {
            notificationConfiguration.buildBodyAsHtmlList();
        }

        if (notificationConfiguration.detailMessages !== undefined &&
            notificationConfiguration.detailMessages !== null &&
            notificationConfiguration.detailMessages.length) {
            return this.showNotificationWithDetail(notificationConfiguration);
        }
        return this.showNotificationWithoutDetail(notificationConfiguration);
    }

    private showNotificationWithoutDetail(notificationConfiguration: INotificationConfiguration): HTMLElement {

        const listContainer = notificationConfiguration.sanitizedBody;

        let notificationType: string;
        const notificationIcon = document.createElement("i");
        switch (notificationConfiguration.notificationType) {
            case NotificationType.Success:
                notificationType = "success";
                notificationIcon.classList.add("fa-check-circle");
                break;
            case NotificationType.Info:
                notificationType = "info";
                notificationIcon.classList.add("fa-info-circle");
                break;
            case NotificationType.Warning:
                notificationType = "warning";
                notificationIcon.classList.add("fa-exclamation-triangle");
                break;
            case NotificationType.Danger:
                notificationType = "danger";
                notificationIcon.classList.add("fa-times-circle");
                break;

            default:
                notificationType = "info";
                notificationIcon.classList.add("fa-info-circle");
        }

        const notificationEl = document.createElement("div");

        [
            "alert",
            "custom-alert",
            "shadow-sm",
            "d-flex",
            `border-${notificationType}`,
            `alert-${notificationType}`,
            `alert-${notificationType}`,
        ].map((x) => notificationEl.classList.add(x));

        if (notificationConfiguration.isDismissible) {
            [
                "alert-dismissible",
                "fade",
                "show",
            ].map((x) => notificationEl.classList.add(x));
        }

        notificationEl.setAttribute("role", "alert");

        const notificationIconWrapper = document.createElement("div");

        [
            "d-flex",
            "align-items-center",
            `bg-${notificationType}`,
            "alert-icon",
        ].map((x) => notificationIconWrapper.classList.add(x));

        notificationIcon.setAttribute("aria-hidden", "true");

        [
            "alert-icon",
            "fas",
            "fa-2x",
            "text-white",
        ].map((x) => notificationIcon.classList.add(x));

        notificationIconWrapper.appendChild(notificationIcon);
        notificationEl.appendChild(notificationIconWrapper);

        const notificationBody = document.createElement("div");

        [
            "d-flex",
            "align-items-center",
            "flex-wrap",
            "w-100",
        ].map((x) => notificationBody.classList.add(x));

        const notificationText = document.createElement("div");
        const alertHeading = document.createElement("span");
        const alertMessagesContainer = document.createElement("div");

        notificationText.classList.add("alert-text");

        [
            "alert-heading",
            "fw-bold",
            `text-${notificationType}`,
        ].map((x) => alertHeading.classList.add(x));

        if (notificationConfiguration.customHeading) {
            alertHeading.innerHTML = notificationConfiguration.customHeading;
        } else {
            this.mLocalization.translateAsync(notificationType, "alert")
                .then(({result}) => {
                    alertHeading.innerHTML = result.value;
                });
        }

        alertMessagesContainer.classList.add("alert-messages-container");

        notificationText.appendChild(alertHeading);
        notificationText.appendChild(alertMessagesContainer);

        this.jQueryInstance(alertMessagesContainer).append(listContainer ?? "");

        notificationBody.appendChild(notificationText);

        const notificationFooter = document.createElement("div");
        const notificationButtons = document.createElement("div");
        [
            "w-100",
            "ms-auto",
            "alert-buttons",
        ].map((x) => notificationFooter.classList.add(x));

        const minorText = document.createElement("div");
        if (notificationConfiguration.minorText != null) {
            minorText.innerHTML = notificationConfiguration.minorText;
            minorText.classList.add("small");
            minorText.classList.add("text-secondary");
            minorText.classList.add("text-wrap");
        }

        notificationFooter.appendChild(minorText);

        if (notificationConfiguration.retryCallback !== undefined) {
            const retryButton = document.createElement("button");
            alertMessagesContainer.classList.add("mb-2");
            retryButton.setAttribute("type", "button");

            [
                "btn",
                `btn-outline-${notificationType}`,
            ].map((x) => retryButton.classList.add(x));

            retryButton.innerHTML = notificationConfiguration.retryText ?? "";

            this.jQueryInstance(retryButton).on("click", notificationConfiguration.retryCallback);

            notificationButtons.appendChild(retryButton);
        }

        if (notificationConfiguration.isDismissible) {
            const closeButton = document.createElement("button");
            closeButton.setAttribute("type", "button");
            closeButton.setAttribute("aria-label", "Close");
            closeButton.setAttribute("data-bs-dismiss", "alert");

            [
                "btn-close",
            ].map((x) => closeButton.classList.add(x));

            notificationButtons.appendChild(closeButton);
        }
        notificationFooter.appendChild(notificationButtons);
        notificationBody.appendChild(notificationFooter);

        notificationEl.appendChild(notificationBody);
        if (notificationConfiguration.container) {
            notificationConfiguration.container.empty();
            notificationConfiguration.container.append(notificationEl);
            notificationConfiguration.container.show();
        }

        if (
            notificationConfiguration.isDismissible &&
            notificationConfiguration.notificationType === NotificationType.Success &&
            notificationConfiguration.autoDismissOnSuccess
        ) {
            setTimeout(() => {
                notificationConfiguration.container?.find(".alert").fadeTo(1000, 0).slideUp(1000, () => {
                    this.destroyNotification(notificationConfiguration.container);
                });
            }, 4000);
        }

        if (notificationConfiguration.timeoutSeconds) {
            setTimeout(() => {
                notificationConfiguration.container?.find(".alert").fadeTo(1000, 0).slideUp(1000, () => {
                    this.destroyNotification(notificationConfiguration.container);
                });
            }, notificationConfiguration.timeoutSeconds * 1000);
        }

        return notificationEl;
    }

    private showNotificationWithDetail(notificationConfiguration: INotificationConfiguration): HTMLElement {
        const listContainer = notificationConfiguration.sanitizedBody;

        let notificationType: string;
        const notificationIcon = document.createElement("i");
        switch (notificationConfiguration.notificationType) {
            case NotificationType.Success:
                notificationType = "success";
                notificationIcon.classList.add("fa-check-circle");
                break;
            case NotificationType.Info:
                notificationType = "info";
                notificationIcon.classList.add("fa-info-circle");
                break;
            case NotificationType.Warning:
                notificationType = "warning";
                notificationIcon.classList.add("fa-exclamation-triangle");
                break;
            case NotificationType.Danger:
                notificationType = "danger";
                notificationIcon.classList.add("fa-times-circle");
                break;

            default:
                notificationType = "info";
                notificationIcon.classList.add("fa-info-circle");
        }

        const notificationEl = document.createElement("div");

        [
            "alert",
            "custom-alert",
            "detail-custom-alert",
            "shadow-sm",
            `border-${notificationType}`,
            `alert-${notificationType}`,
            `alert-${notificationType}`,
        ].map((x) => notificationEl.classList.add(x));

        if (notificationConfiguration.isDismissible) {
            [
                "alert-dismissible",
                "fade",
                "show",
            ].map((x) => notificationEl.classList.add(x));
        }

        notificationEl.setAttribute("role", "alert");

        const notificationContentWrapper = document.createElement("div");
        [
            "d-flex",
        ].map((x) => notificationContentWrapper.classList.add(x));

        const notificationIconWrapper = document.createElement("div");

        [
            "alert-icon",
            "me-1",
        ].map((x) => notificationIconWrapper.classList.add(x));

        notificationIcon.setAttribute("aria-hidden", "true");

        [
            "alert-icon",
            "fas",
            "text-danger",
        ].map((x) => notificationIcon.classList.add(x));

        notificationIconWrapper.appendChild(notificationIcon);

        notificationContentWrapper.appendChild(notificationIconWrapper);

        const notificationBody = document.createElement("div");

        [
            "d-flex",
            "align-items-center",
            "flex-wrap",
            "w-100",
        ].map((x) => notificationBody.classList.add(x));

        const notificationText = document.createElement("div");
        const alertHeading = document.createElement("span");
        const alertMessagesContainer = document.createElement("div");

        notificationText.classList.add("alert-text");

        [
            "alert-heading",
            "fw-bold",
            `text-${notificationType}`,
        ].map((x) => alertHeading.classList.add(x));

        if (notificationConfiguration.customHeading) {
            alertHeading.innerHTML = notificationConfiguration.customHeading;
        } else {
            this.mLocalization.translateAsync(notificationType, "alert")
                .then(({result}) => {
                    alertHeading.innerHTML = result.value;
                });
        }

        alertMessagesContainer.classList.add("alert-messages-container");

        notificationText.appendChild(alertHeading);
        notificationText.appendChild(alertMessagesContainer);

        this.jQueryInstance(alertMessagesContainer).append(listContainer ?? "");

        notificationBody.appendChild(notificationText);

        const notificationFooter = document.createElement("div");
        const notificationButtons = document.createElement("div");
        [
            "w-100",
            "ms-auto",
            "alert-buttons",
        ].map((x) => notificationFooter.classList.add(x));

        notificationButtons.classList.add("ms-auto");

        const minorText = document.createElement("div");
        if (notificationConfiguration.minorText != null) {
            minorText.innerHTML = notificationConfiguration.minorText;
            minorText.classList.add("small");
            minorText.classList.add("text-secondary");
            minorText.classList.add("text-wrap");
        }

        notificationFooter.appendChild(minorText);

        const detailErrorMessageBody = document.createElement("div");
        [
            "detail-error-messages-container",
            "collapse",
        ].map((x) => detailErrorMessageBody.classList.add(x));

        if (notificationConfiguration.detailMessages !== undefined &&
            notificationConfiguration.detailMessages !== null &&
            notificationConfiguration.detailMessages.length
        ) {
            const detailButton = document.createElement("button");

            detailButton.setAttribute("type", "button");

            [
                "btn",
                `btn-outline-${notificationType}`,
                "ms-1",
            ].map((x) => detailButton.classList.add(x));

            this.mLocalization.translateAsync("error-detail")
                .then(({result}) => {
                    detailButton.innerHTML = result.value ?? "";
                });

            this.jQueryInstance(detailButton).on("click", (e) => {
                const detailContainer = this.jQueryInstance(e.currentTarget).parents(".alert").find(".detail-error-messages-container");
                detailContainer.collapse("toggle");
                if (this.jQueryInstance(detailButton).hasClass("active")) {
                    this.jQueryInstance(detailButton).removeClass("active");
                } else {
                    this.jQueryInstance(detailButton).addClass("active");
                }
            });

            notificationButtons.appendChild(detailButton);
            const listContainerEl = document.createElement("ul");

            for (const message of notificationConfiguration.detailMessages) {
                const sanitizedMessage = DOMPurify.sanitize(message);

                const listContainerItemEl = document.createElement("li");
                listContainerItemEl.innerHTML = sanitizedMessage;
                listContainerEl.appendChild(listContainerItemEl);
            }

            const textDetailMessage = document.createElement("div");
            detailErrorMessageBody.appendChild(textDetailMessage);
            detailErrorMessageBody.appendChild(listContainerEl);
        }

        if (notificationConfiguration.retryCallback !== undefined) {
            const retryButton = document.createElement("button");
            alertMessagesContainer.classList.add("mb-2");
            retryButton.setAttribute("type", "button");

            [
                "btn",
                `btn-outline-${notificationType}`,
                "ms-2",
            ].map((x) => retryButton.classList.add(x));

            retryButton.innerHTML = notificationConfiguration.retryText ?? "";

            this.jQueryInstance(retryButton).on("click", notificationConfiguration.retryCallback);

            notificationButtons.appendChild(retryButton);
        }

        if (notificationConfiguration.isDismissible) {
            const closeButton = document.createElement("button");
            closeButton.setAttribute("type", "button");
            closeButton.setAttribute("aria-label", "Close");
            closeButton.setAttribute("data-bs-dismiss", "alert");

            [
                "btn-close",
            ].map((x) => closeButton.classList.add(x));

            notificationButtons.appendChild(closeButton);
        }
        notificationFooter.appendChild(notificationButtons);
        notificationBody.appendChild(notificationFooter);
        notificationContentWrapper.appendChild(notificationBody);

        notificationEl.appendChild(notificationContentWrapper);
        notificationEl.appendChild(detailErrorMessageBody);
        if (notificationConfiguration.container) {
            notificationConfiguration.container.empty();
            notificationConfiguration.container.append(notificationEl);
            notificationConfiguration.container.show();
        }

        if (
            notificationConfiguration.isDismissible &&
            notificationConfiguration.notificationType === NotificationType.Success &&
            notificationConfiguration.autoDismissOnSuccess
        ) {
            setTimeout(() => {
                notificationConfiguration.container?.find(".alert").fadeTo(1000, 0).slideUp(1000, () => {
                    this.destroyNotification(notificationConfiguration.container);
                });
            }, 4000);
        }

        if (notificationConfiguration.timeoutSeconds) {
            setTimeout(() => {
                notificationConfiguration.container?.find(".alert").fadeTo(1000, 0).slideUp(1000, () => {
                    this.destroyNotification(notificationConfiguration.container);
                });
            }, notificationConfiguration.timeoutSeconds * 1000);
        }

        return notificationEl;
    }

    public destroyNotification(notificationContainer?: JQuery, notificationEvent: number | null = null): void {
        if (notificationEvent !== null) {
            clearTimeout(notificationEvent);
        }

        if (!notificationContainer) {
            return;
        }
        const alertBox = notificationContainer.find(".alert");
        if (alertBox.length) {
            alertBox.remove();
        }
        notificationContainer.hide();
    }

    public createCenteredSpinner(spinnerConfiguration: ISpinnerConfiguration): JQuery {
        const sizeClass = spinnerConfiguration.spinnerSize !== undefined ? spinnerConfiguration.spinnerSize : "";
        const marginClass = spinnerConfiguration.marginClass !== undefined ? spinnerConfiguration.marginClass : "mt-2";
        return this.jQueryInstance(lv.create(undefined, `lv-spinner ${sizeClass} lv-mid ${marginClass}`).getElement());
    }

    private destroyModalContents(): void {
        this.alertModalHeaderEl.text("");
        this.alertModalBodyEl.text("");
    }

    public showModal(title: string, body: string): void {
        this.destroyModalContents();
        this.alertModalHeaderEl.text(title);
        this.alertModalBodyEl.text(body);
        this.alertModalEl.modal("show");
    }
    public showModalWithHtmlContent(title: string, bodyHtml: string): void {
        this.destroyModalContents();
        this.alertModalHeaderEl.text(title);
        this.alertModalBodyEl.html(bodyHtml);
        this.alertModalEl.modal("show");
    }

    public hideModal(): void {
        this.alertModalEl.modal("hide");
    }

    public showConfirmModal(
        headerText: string,
        bodyText: string,
        okCallback?: () => void,
        cancelCallback?: () => void,
    ): void {
        this.confirmationModalHeaderEl.text(headerText);
        this.confirmationModalBodyEl.text(bodyText);
        this.confirmationModalCancelButtonEl.on(
            "click",
            () => {
                if (cancelCallback) {
                    cancelCallback();
                }
                this.confirmationModalEl.modal("hide");
                this.confirmationModalCancelButtonEl.off("click");
                this.confirmationModalConfirmButtonEl.off("click");
            },
        );
        this.confirmationModalConfirmButtonEl.on(
            "click",
            () => {
                if (okCallback) {
                    okCallback();
                }
                this.confirmationModalEl.modal("hide");
                this.confirmationModalCancelButtonEl.off("click");
                this.confirmationModalConfirmButtonEl.off("click");
            },
        );
        this.confirmationModalEl.on(
            "hide.bs.modal",
            () => {
                this.confirmationModalCancelButtonEl.off("click");
                this.confirmationModalConfirmButtonEl.off("click");
            },
        );
        this.confirmationModalEl.modal("show");
    }

    public showLoading(loadingMessage: string, loadingContainer: JQuery): HTMLElement {
        const loadingEl = this.showNotification(new NotificationConfiguration({
            notificationType: NotificationType.Info,
            messages: [],
        }));

        const loadingElJq = this.jQueryInstance(loadingEl);
        const loadingSpinner = lv.create(undefined, "lv-spinner tiny lvr-1 lv-mid").getElement();
        loadingElJq.find(".alert-icon, .alert-heading").remove();
        loadingElJq.find(".alert-text")
            .addClass("d-flex align-items-center")
            .prepend(loadingSpinner);
        loadingElJq.find(".alert-messages-container").text(loadingMessage);
        loadingEl.classList.add("loading");

        loadingContainer.html(loadingEl);
        loadingContainer.show();

        return loadingEl;
    }
}
